// DAS-167: 볼린저 밴드 상단/하단 도달 시 보유 포지션 기준 예상 총 손익 계산 (순수 함수, React 무관)
//
// 배경(docs/specs/DAS-167/spec-plan.md 4.2): 대시보드 볼린저 밴드는 BTC 기준 (업비트 KRW ÷ 바이낸스 선물 USD)
// 비율 = 암시적 USDT 환율의 밴드다. safer 보정은 bollingerBandScale.js::applyBandMultiplier 가 수행한다.
// 시나리오는 "환율 R 이 보정 밴드 상단/하단으로 이동"할 때의 포지션 손익을 계산한다.
//
// 계산식 (시나리오 S ∈ {high, middle, low}, 보정 밴드 B = applyBandMultiplier(raw, m), R_S = B.high | B.middle | B.low):
//   ① fiat측 증분:       ΔFiat_t(S) = V_t × (R_S / R_t − 1)
//   ② risky측 KRW 재평가: RiskyKRW_t(S) = U_t × R_S   (U_t = unrealizedProfit USD, 롱/숏 부호 내장)
//   ③ 티커별 예상 손익:   PnL_t(S) = fiat_profit_loss_amount_t + ΔFiat_t(S) + RiskyKRW_t(S)
//   ④ 총 예상 손익:       Total(S) = Σ_t PnL_t(S)
//   ⑤ 현재 대비 증분:     Delta(S) = Total(S) − currentTotal, currentTotal = Σ_t (fiat_pnl_t + U_t × exchangeRate)
//
// 티커 결합·현재가 폴백 로직은 components/dashboard_page/position_table.js 의
// getFiatCurrentPrice / getRiskyCurrentPrice / getRiskyMarkPrice / combinedData 구성과 동일하게 재현한다.
//
// middle(밴드 중앙 회귀) 시나리오: R_S = 보정 밴드의 middle 값. applyBandMultiplier 는 middle 필드를
// 원본 그대로 통과시키므로(중심 산출용으로만 내부에서 쓰고 재계산하지 않음), raw middle 이 유효하면
// 그 값을 그대로 쓴다. raw middle 이 없으면 BollingerGauge(bollinger_gauge.js) 의 Mid 기준선이 이 경우
// midPosition=50% 기본값(=밴드 폭의 정중앙)으로 표시되는 것과 동일하게 (보정 low+보정 high)/2 로 대체한다.
// bestKey/ready 의미론은 변경하지 않는다 — middle 은 참조용 시나리오다.

import { applyBandMultiplier, BAND_MULTIPLIER_DEFAULT } from "./bollingerBandScale";

const toNum = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
};

// 출처: components/dashboard_page/position_table.js::getFiatCurrentPrice
const getFiatCurrentPrice = (position) => {
    const currentPrice = toNum(position?.current_price);
    return currentPrice > 0 ? currentPrice : Number.NaN;
};

// 출처: components/dashboard_page/position_table.js::getRiskyCurrentPrice
const getRiskyCurrentPrice = (position) => {
    const currentPrice = toNum(position?.current_price ?? position?.currentPrice);
    if (currentPrice > 0) {
        return currentPrice;
    }

    const markPrice = toNum(
        position?.markPrice ?? position?.mark_price ?? position?.lastPrice ?? position?.last_price
    );
    if (markPrice > 0) {
        return markPrice;
    }

    const entryPrice = toNum(position?.entryPrice);
    const positionAmt = toNum(position?.positionAmt);
    const unrealizedProfit = toNum(position?.unrealizedProfit);
    if (entryPrice > 0 && Number.isFinite(positionAmt) && positionAmt !== 0 && Number.isFinite(unrealizedProfit)) {
        const inferredPrice = entryPrice + unrealizedProfit / positionAmt;
        return inferredPrice > 0 ? inferredPrice : Number.NaN;
    }

    return Number.NaN;
};

// 출처: components/dashboard_page/position_table.js::getRiskyMarkPrice (render 내부 클로저)
const getRiskyMarkPrice = (riskyMarkPrices, ticker) => {
    if (!riskyMarkPrices) return Number.NaN;
    const symbolKey = `${ticker.toUpperCase()}USDT`;
    const direct = toNum(riskyMarkPrices[symbolKey]);
    if (direct > 0) {
        return direct;
    }
    const alt = toNum(riskyMarkPrices[ticker.toUpperCase()]);
    return alt > 0 ? alt : Number.NaN;
};

const EMPTY_SCENARIO = { ratio: Number.NaN, total: Number.NaN, delta: Number.NaN };

/**
 * 보유 포지션 기준, 밴드 상단/중앙/하단 도달 시 예상 총 손익을 계산한다.
 * @returns {{ready: boolean, hasPosition: boolean, high: object, middle: object, low: object, currentTotal: number, bestKey: 'high'|'low'}}
 */
export const computeBandScenarioPnl = ({
    bollinger,
    multiplier = BAND_MULTIPLIER_DEFAULT,
    exchangeRate,
    fiatPositions,
    riskyPositions,
    riskyMarkPrices,
} = {}) => {
    const exRate = toNum(exchangeRate);

    // 밴드 유효성: low/high 만 유한이면 충분하다. middle 이 없어도 applyBandMultiplier 가
    // low/high 중점을 center 로 대체하므로(bollingerBandScale.js), middle 을 필수 조건에 넣으면
    // BollingerGauge(게이지는 low/high 만으로도 렌더)보다 더 엄격하게 게이팅되어 정렬이 깨진다.
    const rawLow = toNum(bollinger?.low);
    const rawHigh = toNum(bollinger?.high);
    const bandValid = Number.isFinite(rawLow) && Number.isFinite(rawHigh);
    const ready = bandValid && Number.isFinite(exRate) && exRate > 0;

    const fiatArray = Array.isArray(fiatPositions) ? fiatPositions : [];
    const riskyArray = Array.isArray(riskyPositions) ? riskyPositions : [];
    const hasPosition = fiatArray.length > 0 || riskyArray.length > 0;

    if (!ready || !hasPosition) {
        return {
            ready,
            hasPosition,
            high: { ...EMPTY_SCENARIO },
            middle: { ...EMPTY_SCENARIO },
            low: { ...EMPTY_SCENARIO },
            currentTotal: Number.NaN,
            bestKey: "high",
        };
    }

    const adjusted = applyBandMultiplier(bollinger, multiplier);
    let bandLow = toNum(adjusted.low);
    let bandHigh = toNum(adjusted.high);
    // applyBandMultiplier 결과가 역전되는 경우(swap) 방어
    if (Number.isFinite(bandLow) && Number.isFinite(bandHigh) && bandLow > bandHigh) {
        const tmp = bandLow;
        bandLow = bandHigh;
        bandHigh = tmp;
    }
    // middle: raw 값이 유효하면 그대로, 아니면 BollingerGauge 의 midPosition=50% 기본값과
    // 동일한 의미로 보정 low/high 의 중점을 사용한다.
    const rawMiddleAdjusted = toNum(adjusted.middle);
    const bandMiddle = Number.isFinite(rawMiddleAdjusted)
        ? rawMiddleAdjusted
        : Number.isFinite(bandLow) && Number.isFinite(bandHigh)
            ? (bandLow + bandHigh) / 2
            : Number.NaN;

    // 티커별 결합 상태: position_table.js::combinedData 구성과 동일한 결합 키(대문자 ticker) 사용.
    const combined = {};
    const ensureRow = (ticker) => {
        if (!combined[ticker]) {
            combined[ticker] = { fiatCurrentPrice: Number.NaN, riskyCurrentPrice: Number.NaN, V: 0, fiatPnl: 0, U: 0 };
        }
        return combined[ticker];
    };

    // 같은 티커의 fiat 포지션이 여러 건 들어오면 position_table.js::combinedData(L201, first-wins)와
    // 동일하게 첫 번째 항목만 채택한다 — 중복 시 뒤 항목으로 덮어쓰면 두 소스 간 합산 총액이 어긋난다.
    const fiatAssigned = new Set();
    fiatArray.forEach((position) => {
        const currency = position?.currency;
        if (!currency) return;
        const ticker = String(currency).toUpperCase();
        if (fiatAssigned.has(ticker)) return;
        fiatAssigned.add(ticker);
        const row = ensureRow(ticker);

        const balance = toNum(position?.balance);
        const avgBuyPrice = toNum(position?.avg_buy_price);
        const fiatCurrentPrice = getFiatCurrentPrice(position);
        const profitLossAmount = toNum(position?.profit_loss_amount);
        const fiatPositionValue = Number.isFinite(balance) && Number.isFinite(avgBuyPrice) ? balance * avgBuyPrice : Number.NaN;

        // V_t = balance × current_price, current_price 부재 시 fiat_position_value + profit_loss_amount 폴백
        let V;
        if (Number.isFinite(balance) && Number.isFinite(fiatCurrentPrice)) {
            V = balance * fiatCurrentPrice;
        } else if (Number.isFinite(fiatPositionValue) && Number.isFinite(profitLossAmount)) {
            V = fiatPositionValue + profitLossAmount;
        } else {
            V = 0;
        }

        row.fiatCurrentPrice = fiatCurrentPrice;
        row.V = Number.isFinite(V) ? V : 0;
        row.fiatPnl = Number.isFinite(profitLossAmount) ? profitLossAmount : 0;
    });

    riskyArray.forEach((position) => {
        const symbol = position?.symbol;
        if (!symbol) return;
        const ticker = String(symbol).replace("USDT", "").toUpperCase();
        const row = ensureRow(ticker);

        row.riskyCurrentPrice = getRiskyCurrentPrice(position);
        const unrealizedProfit = toNum(position?.unrealizedProfit);
        row.U = Number.isFinite(unrealizedProfit) ? unrealizedProfit : 0;
    });

    let currentTotal = 0;
    let highTotal = 0;
    let middleTotal = 0;
    let lowTotal = 0;

    Object.keys(combined).forEach((ticker) => {
        const row = combined[ticker];

        let riskyCurrentPrice = row.riskyCurrentPrice;
        if (!Number.isFinite(riskyCurrentPrice)) {
            const markPrice = getRiskyMarkPrice(riskyMarkPrices, ticker);
            if (Number.isFinite(markPrice)) {
                riskyCurrentPrice = markPrice;
            }
        }

        // R_t = fiat_current_price / risky_current_price, 산출 불가 시 exchangeRate 폴백 (분모 0 방어)
        let rT = Number.NaN;
        if (
            Number.isFinite(row.fiatCurrentPrice) && row.fiatCurrentPrice > 0 &&
            Number.isFinite(riskyCurrentPrice) && riskyCurrentPrice > 0
        ) {
            rT = row.fiatCurrentPrice / riskyCurrentPrice;
        }
        if (!Number.isFinite(rT) || rT <= 0) {
            rT = exRate;
        }

        const scenarioPnl = (rS) => {
            const deltaFiat = rT > 0 ? row.V * (rS / rT - 1) : 0;
            const riskyKrw = row.U * rS;
            return row.fiatPnl + deltaFiat + riskyKrw;
        };

        currentTotal += row.fiatPnl + row.U * exRate;
        highTotal += scenarioPnl(bandHigh);
        middleTotal += scenarioPnl(bandMiddle);
        lowTotal += scenarioPnl(bandLow);
    });

    // bestKey 는 middle 을 포함하지 않는다 — high/low 양극단 시나리오 비교만으로 결정 (참조 시나리오는 middle).
    const bestKey = highTotal >= lowTotal ? "high" : "low";

    return {
        ready,
        hasPosition,
        high: { ratio: bandHigh, total: highTotal, delta: highTotal - currentTotal },
        middle: { ratio: bandMiddle, total: middleTotal, delta: middleTotal - currentTotal },
        low: { ratio: bandLow, total: lowTotal, delta: lowTotal - currentTotal },
        currentTotal,
        bestKey,
    };
};
